import React, { useRef } from 'react';
import { gsap, useGSAP, prefersReducedMotion } from '../../../lib/gsap';
import { CATEGORIES, ISSUES } from '../data';

/**
 * Kinetic collage of documented headlines and figures that drifts behind the header and
 * parallaxes with the pointer at three depths. Purely decorative; the real content is the list below.
 */
const PICKS = ['raffensperger', 'qatar-jet', 'felony-conviction', 'cecot', 'j6-pardons'];

// Hand-placed slots (percent of the header box): above the portrait's head and down
// its right edge, so the title, the face and the stats row stay clear.
const SLOTS = [
   { x: 64, y: 10, d: 3 },
   { x: 84, y: 14, d: 2 },
   { x: 68, y: 25, d: 1 },
   { x: 89, y: 33, d: 3 },
   { x: 90, y: 56, d: 2 },
];

const HeadlineCollage = () => {
   const root = useRef<HTMLDivElement>(null);
   const items = PICKS.map(id => ISSUES.find(i => i.id === id)!).filter(Boolean);

   useGSAP(
      () => {
         const tiles = gsap.utils.toArray<HTMLElement>('[data-tile]');
         if (prefersReducedMotion()) return;

         gsap.from(tiles, {
            autoAlpha: 0,
            scale: 0.85,
            filter: 'blur(8px)',
            stagger: { each: 0.07, from: 'random' },
            duration: 1.6,
            delay: 0.6,
         });

         // Idle drift
         tiles.forEach((t, i) =>
            gsap.to(t, {
               y: `+=${8 + (i % 3) * 6}`,
               x: `+=${(i % 2 ? -1 : 1) * (4 + (i % 4) * 3)}`,
               duration: 4 + (i % 5),
               ease: 'sine.inOut',
               yoyo: true,
               repeat: -1,
            })
         );

         // Pointer parallax by depth
         const layers = [1, 2, 3].map(depth => {
            const el = root.current!.querySelector<HTMLElement>(`[data-depth="${depth}"]`)!;
            return {
               depth,
               x: gsap.quickTo(el, 'x', { duration: 1.2, ease: 'power3.out' }),
               y: gsap.quickTo(el, 'y', { duration: 1.2, ease: 'power3.out' }),
            };
         });
         const move = (e: PointerEvent) => {
            const nx = e.clientX / window.innerWidth - 0.5;
            const ny = e.clientY / window.innerHeight - 0.5;
            layers.forEach(l => {
               l.x(-nx * l.depth * 18);
               l.y(-ny * l.depth * 12);
            });
         };
         window.addEventListener('pointermove', move, { passive: true });

         // Scroll: collage spreads and fades
         gsap.to('[data-depth]', {
            yPercent: (i: number) => -(i + 1) * 8,
            autoAlpha: 0,
            ease: 'none',
            scrollTrigger: { trigger: root.current, start: 'top top', end: 'bottom top', scrub: true },
         });

         return () => window.removeEventListener('pointermove', move);
      },
      { scope: root }
   );

   return (
      <div ref={root} aria-hidden="true" className="pointer-events-none absolute inset-0 hidden overflow-hidden xl:block">
         {[1, 2, 3].map(depth => (
            <div key={depth} data-depth={depth} className="absolute inset-0">
               {items.map((issue, i) =>
                  SLOTS[i].d === depth ? (
                     <div
                        key={issue.id}
                        data-tile
                        className={`absolute max-w-[13rem] border-l pl-3 ${
                           depth === 3 ? 'border-signal/60' : depth === 2 ? 'border-white/25' : 'border-white/10'
                        }`}
                        style={{
                           left: `${SLOTS[i].x}%`,
                           top: `${SLOTS[i].y}%`,
                           opacity: depth === 3 ? 0.9 : depth === 2 ? 0.55 : 0.3,
                           transform: `scale(${0.8 + depth * 0.1})`,
                        }}
                     >
                        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-signal-soft">
                           {issue.date.slice(0, 4)} · {CATEGORIES[issue.category]}
                        </p>
                        <p className="mt-1 font-display text-xl leading-tight text-bone">
                           {issue.figure || issue.title}
                        </p>
                     </div>
                  ) : null
               )}
            </div>
         ))}
      </div>
   );
};

export default HeadlineCollage;
